import { keepHonorifics, quranText } from '../data/islam';
import { EVIDENCE, type Evidence } from '../data/islamEvidence';
import { WHY_NAME } from '../data/name';
import { settings } from '../state';
import { SayArabic, SayQuran } from './SayArabic';

// A quoted text, word for word as approved (src/data/islamEvidence.ts): its lead line ("Abū Dharr ﵁ said:"), its Arabic,
// its English and its reference. A verse always shows its Arabic, in the King Fahd Complex's face; the hadith and the
// scholars' words show theirs with the Arabic setting on (BUILD-SPEC rule G4).

/** Approved verses the lessons and cards give as "Allah said:" (the owner's A14, 14 October 2026, and item 43, 22 October
 *  2026): [Al-Baqarah: 2:189] as "Why Ahillah?" gives it, and [Al-Naḥl: 16:16] and [Fuṣṣilat: 41:37], which "The stars
 *  in Islam" quotes without a lead. */
const LESSON_VERSES: Record<string, Evidence> = {
  'Q-2:189': { ref: WHY_NAME.verse.ref, arabic: WHY_NAME.verse.arabic, english: WHY_NAME.verse.english, grade: '', lead: 'Allah said:' },
  'Q-16:16': { ...EVIDENCE['Q-01'], lead: 'Allah said:' },
  'Q-41:37': { ...EVIDENCE['Q-07'], lead: 'Allah said:' },
};

/** The approved text with this id, if there is one. */
export const quoteOf = (id: string): Evidence | undefined => EVIDENCE[id] ?? LESSON_VERSES[id];

export function Quote({ id }: { id: string }) {
  const e = quoteOf(id);
  if (!e) return null;
  const verse = id.startsWith('Q-');
  return (
    <blockquote class="islam-quote">
      {e.lead && <p class="islam-lead">{keepHonorifics(e.lead)}</p>}
      {verse ? (
        <>
          <p class="islam-ar quran-text" lang="ar" dir="rtl">{quranText(e.arabic)}</p>
          <SayQuran text={e.arabic} />
        </>
      ) : (
        settings.value.arabic && (
          <>
            <p class="islam-ar" lang="ar" dir="rtl">{e.arabic}</p>
            <SayArabic text={e.arabic} />
          </>
        )
      )}
      <p>{keepHonorifics(e.english)}</p>
      <cite>{keepHonorifics(e.ref)}{e.grade ? ` · ${e.grade}` : ''}</cite>
    </blockquote>
  );
}

/** Several quoted texts in order. */
export const Quotes = ({ ids }: { ids: string[] }) => <>{ids.map((id) => <Quote key={id} id={id} />)}</>;
